import { Field, Input, Stack, Text } from '@components/ui';
import { formatMoney } from '@features/buyer/hire/formatMoney';
import { HireStepActions } from '@features/buyer/hire/hire-r8tr/hire-step-actions';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import { getCategoryTypeOptions } from '@features/buyer/hire/job-price-calculator/category-pickers';
import {
  CATEGORY_TYPE,
  type CategoryType,
} from '@features/buyer/hire/job-price-calculator/priceCalculator';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronDown } from 'react-icons/io5';

export type JobPriceFieldsSelection = {
  categoryType: CategoryType | null;
  category: { name?: string } | null;
  subCategory: { name?: string } | null;
  openTypePicker: () => void;
  openCategoryPicker: () => void;
  openSubCategoryPicker: () => void;
};

export type JobPriceFieldsPricing = {
  askingPrice: string;
  onAskingPriceChange: (event: ChangeEvent<HTMLInputElement>) => void;
  buyerCost: number | null;
  buyerCostError: string;
  isBuyerCostLoading: boolean;
};

export type JobPriceFieldsTerms = {
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  onOpenTerms: () => void;
};

interface JobPriceFieldsProps extends HireStepNavigation {
  selection: JobPriceFieldsSelection;
  pricing: JobPriceFieldsPricing;
  terms: JobPriceFieldsTerms;
  canSubmit: boolean;
  onSubmit: () => void;
}

const pickerSx = (selected: boolean, enabled = true): SxProps<Theme> => ({
  display: 'flex',
  width: '100%',
  cursor: enabled ? 'pointer' : 'not-allowed',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 1.5,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  px: 2,
  py: 1.25,
  textAlign: 'left',
  font: 'inherit',
  fontSize: 14,
  color: selected ? 'text.primary' : 'text.secondary',
  opacity: enabled ? 1 : 0.4,
  transition: 'border-color 0.2s ease',
  '& svg': { flexShrink: 0, opacity: 0.5 },
  '&:hover:enabled, &:focus-visible': { borderColor: 'text.primary' },
});

const quoteText = (
  loading: boolean,
  cost: number | null,
  hasError: boolean,
  t: (key: string) => string
): { amount: string; hint: string } => {
  if (loading) {
    return { amount: '', hint: t('hire.quoteCalculating') };
  }
  if (hasError) {
    return { amount: '—', hint: t('hire.quoteFixAskingPrice') };
  }
  if (cost === null) {
    return {
      amount: '—',
      hint: t('hire.quotePickCategory'),
    };
  }
  return {
    amount: formatMoney(cost),
    hint: t('hire.quoteFlatFee'),
  };
};

export function JobPriceFields({
  selection,
  pricing,
  terms,
  canSubmit,
  onSubmit,
  onBack,
  onCancel,
}: JobPriceFieldsProps): JSX.Element {
  const { t } = useTranslation();
  const quote = quoteText(
    pricing.isBuyerCostLoading,
    pricing.buyerCost,
    Boolean(pricing.buyerCostError),
    t
  );
  const isNonAutomotive =
    selection.categoryType === CATEGORY_TYPE.NON_AUTOMOTIVE;

  return (
    <Stack gap="xs" sx={{ flex: 1 }}>
      <Field label={t('hire.type')}>
        <Box
          component="button"
          type="button"
          sx={pickerSx(Boolean(selection.categoryType))}
          onClick={selection.openTypePicker}
        >
          <span>
            {selection.categoryType
              ? getCategoryTypeOptions().find(
                  (option) => option.value === selection.categoryType
                )?.label
              : t('hire.selectTypePlaceholder')}
          </span>
          <IoChevronDown size={16} />
        </Box>
      </Field>

      <Field
        label={
          isNonAutomotive
            ? t('hire.categoryRequired')
            : t('hire.vehicleCategoryRequired')
        }
      >
        <Box
          component="button"
          type="button"
          sx={pickerSx(
            Boolean(selection.category),
            Boolean(selection.categoryType)
          )}
          onClick={() =>
            selection.categoryType && selection.openCategoryPicker()
          }
        >
          <span>
            {selection.category
              ? selection.category.name
              : t('hire.selectCategoryPlaceholder')}
          </span>
          <IoChevronDown size={16} />
        </Box>
      </Field>

      <Field
        label={
          isNonAutomotive
            ? t('hire.subcategoryRequired')
            : t('hire.vehicleSubcategoryRequired')
        }
      >
        <Box
          component="button"
          type="button"
          sx={pickerSx(
            Boolean(selection.subCategory),
            Boolean(selection.category)
          )}
          onClick={() =>
            selection.category && selection.openSubCategoryPicker()
          }
        >
          <span>
            {selection.subCategory
              ? selection.subCategory.name
              : t('hire.selectSubcategoryPlaceholder')}
          </span>
          <IoChevronDown size={16} />
        </Box>
      </Field>

      <Field
        label={t('hire.askingPrice')}
        error={pricing.buyerCostError || undefined}
      >
        <Input
          type="number"
          placeholder={
            selection.subCategory ? '25,000' : t('hire.selectSubcategoryFirst')
          }
          disabled={!selection.subCategory}
          sx={{ opacity: selection.subCategory ? 1 : 0.4 }}
          value={pricing.askingPrice}
          onChange={pricing.onAskingPriceChange}
          inputProps={{ min: 0, inputMode: 'decimal' }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          invalid={Boolean(pricing.buyerCostError)}
        />
      </Field>

      <Box
        aria-live="polite"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          mt: 1.5,
          px: 2.5,
          py: 2,
        }}
      >
        <Box>
          <Text as="p" variant="label" sx={{ fontWeight: 700 }}>
            {t('hire.inspectionFee')}
          </Text>
          <Text
            variant="caption"
            tone="muted"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {quote.hint}
          </Text>
        </Box>
        {pricing.isBuyerCostLoading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          <Text
            as="p"
            variant="title"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {quote.amount}
          </Text>
        )}
      </Box>

      <FormControlLabel
        sx={{ mx: 0, mt: 3, mb: 1.5, alignItems: 'center', gap: 0.5 }}
        control={
          <Checkbox
            checked={terms.accepted}
            onChange={() => terms.onAcceptedChange(!terms.accepted)}
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '& .MuiSvgIcon-root': { fontSize: 20 },
              '&.Mui-checked': { color: 'primary.main' },
            }}
          />
        }
        label={
          <Text variant="body" tone="muted" sx={{ fontSize: 14 }}>
            {t('hire.agreeToTerms')}{' '}
            <Box
              component="button"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                terms.onOpenTerms();
              }}
              sx={{
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                font: 'inherit',
                color: 'text.primary',
                fontWeight: 600,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                cursor: 'pointer',
              }}
            >
              {t('hire.termsLink')}
            </Box>
          </Text>
        }
      />

      <HireStepActions
        onBack={onBack}
        onCancel={onCancel}
        onPrimary={onSubmit}
        primaryDisabled={!canSubmit}
      />
    </Stack>
  );
}
